/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import React from 'react';
import { defineMessages } from 'react-intl';
import { DeletePromptModal, DeletePromptModalProps } from 'src/core/components/DeletePromptModal';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';

export type DeleteEntityModalProps = DeletePromptModalProps<object> & {
    entityNames: string[];
    usedByEntityNames?: string[];
    usedByIntentNames?: string[];
};

const messages = defineMessages({
    deleteEntityTitle: {
        id: 'entities.deleteEntityModal.deleteEntityTitle',
        defaultMessage: 'Delete entity?'
    },
    deleteMultipleEntitiesTitle: {
        id: 'entities.deleteEntityModal.deleteMultipleEntitiesTitle',
        defaultMessage: 'Delete entities?'
    },
    deleteMultipleEntitiesMessage: {
        id: 'entities.deleteEntityModal.deleteMultipleEntitiesMessage',
        defaultMessage: 'Are you sure you want to delete the selected entities?'
    },
    deleteEntityMessage: {
        id: 'entities.deleteEntityModal.deleteEntityMessage',
        defaultMessage: 'Are you sure you want to delete entity "{entityName}" ?'
    },
    usedByDeleteMessage: {
        id: 'entities.deleteEntityModal.usedByDeleteMessage',
        defaultMessage: 'It is used as a feature by:'
    },
    entities: {
        id: 'entities.deleteEntityModal.entities',
        defaultMessage: 'Entities:'
    },
    intents: {
        id: 'entities.deleteEntityModal.intents',
        defaultMessage: 'Intents:'
    },
    andMore: {
        id: 'entities.deleteEntityModal.andMore',
        defaultMessage: ', and more.'
    },
    usedByMultipleDeleteMessage: {
        id: 'entities.deleteEntityModal.usedByMultipleDeleteMessage',
        defaultMessage: 'Some of these entities are used as features.'
    }
});

const { styleComponent } = getStylistV2('DeleteEntityModal');

const Wrapper = styleComponent<IStackProps>(Stack)('Wrapper', {
    flexWrap: 'wrap',
    whiteSpace: 'pre-wrap'
});

/**
 * A modal to confirm deleting entity/entities.
 */
export const DeleteEntityModal = (props: DeleteEntityModalProps) => {
    const { entityNames = [], usedByEntityNames = [], usedByIntentNames = [], ...rest } = props;

    const { localize } = useLocalization();

    const isSingleDeletion = entityNames.length === 1;
    const usedByOtherModels = usedByEntityNames.length > 0 || usedByIntentNames.length > 0;

    /**
     * Displays a list of entities/intents names along with the respective title.
     */
    const usedByRenderer = (usedByList: string[], subTitle: string) =>
        usedByList.length > 0 && (
            <Stack horizontal tokens={{ childrenGap: 7 }}>
                <LuisText fontWeight={500}>
                    <li>{subTitle}</li>
                </LuisText>
                <Wrapper>
                    {usedByList
                        .sort()
                        .slice(0, 10)
                        .join(', ')}
                    {usedByList.length > 10 && localize(messages.andMore)}
                </Wrapper>
            </Stack>
        );

    return (
        <DeletePromptModal
            {...rest}
            dialogContentProps={{ title: localize(isSingleDeletion ? messages.deleteEntityTitle : messages.deleteMultipleEntitiesTitle) }}>
            <Stack tokens={{ childrenGap: 10 }}>
                <LuisText>
                    {isSingleDeletion
                        ? localize(messages.deleteEntityMessage, { entityName: entityNames[0] })
                        : localize(messages.deleteMultipleEntitiesMessage)}
                </LuisText>

                {isSingleDeletion && usedByOtherModels && (
                    <>
                        <LuisText data-automation-id="usedByDeleteMessage">{localize(messages.usedByDeleteMessage)}</LuisText>

                        {usedByRenderer(usedByEntityNames, localize(messages.entities))}
                        {usedByRenderer(usedByIntentNames, localize(messages.intents))}
                    </>
                )}

                {!isSingleDeletion && usedByOtherModels && (
                    <LuisText data-automation-id="usedByMultipleDeleteMessage">{localize(messages.usedByMultipleDeleteMessage)}</LuisText>
                )}
            </Stack>
        </DeletePromptModal>
    );
};
